import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import Button from '../../components/common/Button';
import FormField from '../shared/FormField';
import { formsAndPrecedentsApi, extractOne } from '../../api/facades';

const inputClass = 'w-full rounded-lg border border-gray-300 bg-white dark:bg-slate-800 dark:border-gray-600 py-2 px-3 text-sm text-slate-700 dark:text-slate-200 focus:border-primary-300 focus:outline-none';

const FormEditPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const numId = id ? parseInt(id, 10) : NaN;
  const [name, setName] = useState('');
  const [document_name, setDocumentName] = useState('');
  const [keywords, setKeywords] = useState('');
  const [description, setDescription] = useState('');

  const { data: res, isLoading } = useQuery({
    queryKey: ['forms-and-precedents', numId],
    queryFn: () => formsAndPrecedentsApi.get(numId),
    enabled: !Number.isNaN(numId),
  });
  const row = extractOne(res);

  useEffect(() => {
    if (!row || typeof row !== 'object') return;
    const r = row as any;
    if (r.name != null) setName(String(r.name));
    if (r.document_name != null) setDocumentName(String(r.document_name));
    if (r.keywords != null) setKeywords(String(r.keywords));
    if (r.description != null) setDescription(String(r.description));
  }, [row]);

  const update = useMutation({
    mutationFn: () => formsAndPrecedentsApi.update(numId, { name: name || undefined, document_name: document_name || undefined, keywords: keywords || undefined, description: description || undefined }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['forms-and-precedents'] });
      toast.success('Form or precedent updated.');
      navigate(`/dashboard/forms-and-precedents/${numId}`);
    },
    onError: (e: any) => toast.error(e?.message || 'Update failed.'),
  });

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    update.mutate();
  };

  if (Number.isNaN(numId)) {
    return (
      <div className="bg-white dark:bg-slate-800/50 rounded-2xl p-6 border"><p className="text-slate-600">Invalid ID.</p><Button variant="secondary" className="mt-4" onClick={() => navigate(-1)}>Back</Button></div>
    );
  }

  return (
    <div className="bg-white dark:bg-slate-800/50 rounded-2xl p-6 border border-gray-100 dark:border-gray-700">
      <h1 className="text-xl font-bold text-slate-700 dark:text-slate-200 mb-4">Edit Form or Precedent</h1>
      {isLoading ? <p className="text-slate-500">Loading…</p> : (
        <form onSubmit={onSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="md:col-span-2">
            <FormField label="Name">
              <input className={inputClass} value={name} onChange={(e) => setName(e.target.value)} />
            </FormField>
          </div>
          <FormField label="Document name">
            <input className={inputClass} value={document_name} onChange={(e) => setDocumentName(e.target.value)} />
          </FormField>
          <FormField label="Keywords">
            <input className={inputClass} value={keywords} onChange={(e) => setKeywords(e.target.value)} />
          </FormField>
          <div className="md:col-span-2">
            <FormField label="Description">
              <textarea className={inputClass} rows={3} value={description} onChange={(e) => setDescription(e.target.value)} />
            </FormField>
          </div>
          <div className="md:col-span-2 flex gap-2 mt-2">
            <Button variant="secondary" type="button" onClick={() => navigate(-1)}>Cancel</Button>
            <Button variant="primary" type="submit" disabled={update.isPending}>{update.isPending ? 'Saving…' : 'Save'}</Button>
          </div>
        </form>
      )}
    </div>
  );
};

export default FormEditPage;
